<template>
  <article class="view-organizationsettings">
    <template v-if="organization && organizationRole">
      <organization-join-view
        v-if="!membership?.accepted"
        class="view-organizationsettings__join"
        :organization-slug="organizationSlug"
      />
      <organization-setup-view
        v-else-if="organizationRole.canManageOrganization && organization.setupCompletedAt == null"
        :organization-slug="organizationSlug"
      />
      <template v-else-if="membership?.accepted">
        <header class="view-organizationsettings__header">
          <h1>{{ organization.name }}</h1>
          <button
            v-if="organizationRole.canManageOrganization && !expired"
            type="button"
            class="button-icon"
            @click="startEditingName()"
          >
            <lucide-icon :name="state.isEditing ? 'x' : 'pencil'" />
          </button>
        </header>

        <form
          v-if="state.isEditing"
          class="view-organizationsettings__editform"
          @submit.prevent="saveName()"
        >
          <input-text
            v-model:value="data.newOrganizationName"
            ref="organizationNameInput"
            :label="$t('labels.name')"
            :required="true"
            autocomplete="new-organization-name"
            :error="
              organizationName.isFree == false
                ? $t('messages.nameTaken')
                : undefined
            "
            @update:value="onOrganizationNameUpdate()"
          />
          <div>
            <input-text 
              v-model:value="data.newOrganizationSlug"
              pattern="[a-z0-9\-]+"
              :error="nameError?.fields?.slug"
              :label="$t('labels.slug')"
            />
            <div class="message-notice mt-2 p-2">
              <lucide-icon name="info" />
              {{ $t("texts.slugExplanation") }}
            </div>
          </div>

          <div class="line_flex">
            <button class="button" type="submit" :disabled="savingName">
              {{ $t("labels.save") }}
            </button>
            <button
              class="button button_secondary"
              type="button"
              @click="state.isEditing = false"
            >
              {{ $t("labels.abort") }}
            </button>
          </div>
        </form>

        <section v-if="expired" class="message-error p-4">
          <h3 class="mt-2">{{ $t("messages.organizationExpired") }}</h3>
          <p>
            {{
              $t("texts.organizationExpired", {
                deleteDay: dayTheOrganizationWillBeDeletedOn
              })
            }}
          </p>
          <p>
            {{ $t("messages.reactivateOrganization") }}
          </p>

          <button
            v-if="subscription && subscription.editable"
            class="button button_secondary"
            type="button"
            @click="tryReactivatingSubscription()"
          >
            {{ $t("actions.renewSubscription") }}
          </button>
        </section>

        <template v-if="organizationRole.canSeeMembers">
          <!-- MEMBERS -->

          <div class="line_flex mt-6 -v-center mb-5">
            <h3 class="subtitle mv-0">{{ $t("headings.users") }}</h3>
            <div v-if="canManageMembers">
              ( {{ members.length }} /
              {{
                licenseLimit == Infinity ? $t("labels.unlimited") : licenseLimit
              }}
              )

              <small
                v-if="
                  subscription?.nextRenew.seats != null &&
                  subscription?.nextRenew.seats != licenseLimit
                "
                class="ml-3"
              >
                {{
                  $t("texts.newLimit", {
                    newLimit: subscription.nextRenew.seats,
                    date: renewalDateFormatted
                  })
                }}
              </small>
            </div>
          </div>

          <h4>{{ $t("headings.internalUsers") }}</h4>
          <p>{{ $t("text.internalUsersExplanation") }}</p>
          <member-list
            :members="internalMembers"
            :roles="roles"
            :can-manage-members="canManageMembers"
            :can-see-emails="organizationRole.canSeeEmails"
            :user-membership="membership"
            :can-set-valid-until="true"
            :assignable-role-codes="assignableRoles"
            :max-members="licenseLimit"
            member-word="user"
            @update="tryUpdatingMember"
            @delete="tryRemovingMember"
          />
          <button
            v-if="canManageMembers"
            type="button"
            class="button mt-4"
            :disabled="members.length >= licenseLimit"
            @click="startInvitingMembers"
          >
            {{ $t("actions.addMoreUsers") }}
          </button>
          <member-invitation-popup
            :visible="state.isMemberInvitationPopupOpen"
            :title="$t('actions.addMoreMembers')"
            :existing-emails="memberEmails"
            :invite-function="sendInvitations"
            :max-invitations="licenseLimit"
            :roles="rolesUserCanAssign"
            :can-set-valid-until="true"
            default-role-code="MEMBER"
            member-word="user"
            @close="state.isMemberInvitationPopupOpen = false"
          />

          <template v-if="organizationRole.canSeeExternals">
            <!-- external Members -->

            <h4 class="mt-6">{{ $t("headings.externalUsers") }}</h4>
            <p>{{ $t("text.externalMembersExplanation") }}</p>
            <member-list
              :members="externalMembers"
              :roles="roles"
              :can-manage-members="canManageMembers"
              :can-see-emails="organizationRole.canSeeEmails"
              :user-membership="membership"
              :can-set-valid-until="true"
              :assignable-role-codes="assignableRoles"
              :max-members="licenseLimit"
              member-word="user"
              @update="tryUpdatingMember"
              @delete="tryRemovingMember"
            />
            <button
              v-if="canManageMembers"
              type="button"
              class="button mt-4"
              :disabled="members.length >= licenseLimit"
              @click="startInvitingExternals"
            >
              {{ $t("actions.addMoreUsers") }}
            </button>
            <member-invitation-popup
              :visible="state.isExternalInvitationPopupOpen"
              :title="$t('actions.addMoreMembers')"
              :existing-emails="memberEmails"
              :invite-function="sendInvitations"
              :max-invitations="licenseLimit"
              :roles="rolesUserCanAssign"
              :can-set-valid-until="true"
              default-role-code="EXTERN"
              member-word="user"
              @close="state.isExternalInvitationPopupOpen = false"
            />
          </template>

          <hr class="mt-6" />
        </template>
        <template v-else>
          <p>{{ $t("texts.providesLicense", { provider: organization.name }) }}</p>
          <p>{{ $t("texts.externalMember") }}</p>
        </template>

        <template v-if="organizationRole.canSeeMembers">
          <!-- TEAMS -->

          <div class="line_flex mt-6 -v-center mb-5">
            <h3 class="subtitle mt-0 mb-0">{{ $t("headings.teams") }}</h3>
            <div v-if="canManageTeams">
              ( {{ teams.length }} /
              {{
                workspaceLimit == Infinity
                  ? $t("labels.unlimited")
                  : workspaceLimit
              }}
              )

              <small
                v-if="
                  subscription?.workspaceLimitOnRenew != null &&
                  subscription?.workspaceLimitOnRenew != workspaceLimit
                "
                class="ml-3"
              >
                {{
                  $t("texts.newLimit", {
                    newLimit: subscription.workspaceLimitOnRenew,
                    date: renewalDateFormatted
                  })
                }}
              </small>
            </div>
          </div>

          <template v-if="teams.length > 0">
            <table class="view-organizationsettings__teams table-grid">
              <thead>
                <tr>
                  <th class="table__heading">{{ $t("labels.name") }}</th>
                  <th class="table__heading">{{ $t("headings.members") }}</th>
                  <th v-if="canManageTeams" class="table__heading"></th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="team in teams"
                  :key="team.id"
                  class="view-organizationsettings__team"
                >
                  <td class="pv-3">
                    <router-link
                      :to="{
                        name: 'settings-team',
                        params: {
                          organizationSlug: organizationSlug,
                          teamSlug: team.slug
                        }
                      }"
                    >
                      {{ team.name }}
                    </router-link>
                  </td>
                  <td class="pv-3 view-organizationsettings__teammembers">
                    <user-avatar
                      v-for="member in team.members"
                      :key="member.id"
                      :user="member"
                      :is-admin="member.roleCode === ROLE_CODES.ADMIN"
                      class="view-organizationsettings__teammember"
                    />
                  </td>
                  <td v-if="canManageTeams">
                    <button
                      :title="$t('actions.deleteTeam')"
                      class="button-icon"
                      type="button"
                      @click="tryDestroyingTeam(team)"
                    >
                      <lucide-icon name="trash-2" />
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </template>
          <p v-else class="message-info">
            {{ $t("messages.noTeams") }}
          </p>

          <button
            v-if="canManageTeams"
            type="button"
            class="button mt-4"
            :disabled="teams.length >= workspaceLimit"
            @click="openTeamCreationPopup"
          >
            {{ $t("actions.createTeam") }}
          </button>

          <popup-dialog
            :visible="state.isTeamCreationPopupOpen"
            :close-button-visible="true"
            :title="$t('actions.createTeam')"
            class="view-organizationsettings__teampopup"
            @close="state.isTeamCreationPopupOpen = false"
          >
            <div class="p-6">
              <team-create-form
                :organization-members="members"
                :organization-slug="organizationSlug"
                @create="state.isTeamCreationPopupOpen = false"
              />
            </div>
          </popup-dialog>
        </template>

        <div v-if="membership?.roleCode != 'OWNER'">
          <hr class="mt-6" />
          <button
            class="button button_secondary"
            type="button"
            @click="tryLeavingOrganization"
          >
            {{ $t("actions.leaveOrganization") }}
          </button>
        </div>

        <template v-if="organizationRole?.canManageOrganization">
          <hr class="mt-6" />
          <h3 class="subtitle mt-6 mb-5">
            {{ $t("headings.organizationSettings") }}
          </h3>
          <input-checkbox
            :value="organization.membersCanSeeEmails"
            :label="$t('labels.usersCanSeeEmails')"
            @update:value="updateMembersCanSeeEmails($event)"
          />

          <subscription-card
            v-if="subscription"
            class="mt-4"
            :subscription-id="subscription.id"
          />
          <loading-spinner v-else-if="subscriptionStore.loading" style="width: 40rem" />
          <p v-else>{{ $t("errors.subscriptionNotLoaded") }}</p>

          <hr />

          <div v-if="owner">
            <h4 class="mb-2 label">
              {{ $t("labels.owner") }}
            </h4>
            <div class="view-organizationsettings__assistant">
              <user-avatar :user="owner" />
              <span>{{ owner?.name }}</span>
              <span>({{ owner?.email }})</span>
            </div>
          </div>

          <template v-if="assistantOwners.length > 0 || !expired">
            <h4 class="mb-2 label">
              {{ $t("labels.deputies") }}
            </h4>
            <div
              v-for="assistant in assistantOwners"
              :key="assistant.id"
              class="view-organizationsettings__assistant"
            >
              <user-avatar :user="assistant" />
              <span v-if="assistant?.name">
                {{ assistant?.name }}
              </span>
              <span v-else>{{ $t("labels.pending") }}</span>
              ({{ assistant.email }})
            </div>
            <p
              v-if="assistantOwners.length == 0"
              class="message-info mt-0 mb-0"
            >
              {{ $t("texts.assistantOwnersInstruction") }}
            </p>
            <button
              v-if="!expired"
              class="button button_secondary mt-3"
              type="button"
              @click="state.isCreatingAssistants = true"
            >
              {{ $t("actions.setAssistantOwners") }}
            </button>

            <create-assistants-popup
              :visible="state.isCreatingAssistants"
              :owner="owner"
              :members="activeMembers"
              :assistant-owners="assistantOwners"
              @close="state.isCreatingAssistants = false"
              @save="setAssistantOwners"
              @update:owner="setOwner"
            />
          </template>

          <template v-if="expired">
            <hr />
            <button
              class="button button_secondary"
              type="button"
              @click="tryDestroyingOrganization()"
            >
              <template v-if="!expired">
                {{ $t("actions.destroyOrganization") }}
              </template>
              <template v-else>
                {{ $t("actions.deleteOrganization") }}
              </template>
            </button>
          </template>
        </template>
      </template>
    </template>

    <template v-else>
      <!-- NO ORGANIZATION ACCESS -->
      <div v-if="teams.length > 0">
        <header class="view-organizationsettings__header">
          <h1>{{ teams[0].organizationName }}</h1>
        </header>
        <p>{{ $t("texts.notOrganizationMember") }}</p>

        <table class="view-organizationsettings__teams table-grid">
          <thead>
            <tr>
              <th class="table__heading">{{ $t("labels.name") }}</th>
              <th class="table__heading">{{ $t("headings.members") }}</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="team in teams"
              :key="team.id"
              class="view-organizationsettings__team"
            >
              <td class="pv-3">
                <router-link
                  :to="{
                    name: 'settings-team',
                    params: {
                      organizationSlug: organizationSlug,
                      teamSlug: team.slug
                    }
                  }"
                >
                  {{ team.name }}
                </router-link>
              </td>
              <td class="pv-3 view-organizationsettings__teammembers">
                <user-avatar
                  v-for="member in team.members"
                  :key="member.id"
                  :user="member"
                  :is-admin="member.roleCode === ROLE_CODES.ADMIN"
                  class="view-organizationsettings__teammember"
                />
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <error-page-content
        v-else
        error-code="organization_not_found"
        :title="$t('errors.organizationNotFound')"
        :status="404"
      />
    </template>
  </article>
</template>

<script setup lang="ts">
import { computed, nextTick, reactive, ref, useTemplateRef, watch } from "vue";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { useToast } from "vue-toastification";
import { formatDate } from "@/i18n";
import * as API from "@/api/API";
import { showConfirm, showError } from "@core/ui/alert/alert";

import { useUser } from "@/api/user.store";
import { useOrganizations } from "@manager/organizations/organizations.store";
import { useWorkspaces } from "@/api/workspaces.store";

import PopupDialog from "@core/ui/PopupDialog.vue";
import LucideIcon from "@core/ui/LucideIcon.vue";
import LoadingSpinner from "@core/ui/LoadingSpinner.vue";
import InputText from "@core/input/InputText.vue";
import InputCheckbox from "@core/input/InputCheckbox.vue";

import UserAvatar from "@manager/common/UserAvatar.vue";
import MemberList from "@manager/members/MemberList.vue";
import TeamCreateForm from "@manager/teams/TeamCreateForm.vue";
import MemberInvitationPopup from "@manager/members/MemberInvitationPopup.vue";
import SubscriptionCard from "@manager/subscriptions/SubscriptionCard.vue";
import OrganizationJoinView from "@manager/organizations/OrganizationJoinView.vue";
import OrganizationSetupView from "@manager/organizations/OrganizationSetupView.vue";
import CreateAssistantsPopup from "@manager/organizations/CreateAssistantsPopup.vue";

import ErrorPageContent from "@core/errors/ErrorPageContent.vue";
import { useSubscriptions, type Subscription } from "@/api/subscriptions.store";
import { debounce } from "lodash-es";
import { BadRequestError } from "@core/errors/Errors";

import type { OrganizationMember, OrganizationRole } from "@/api/Organization";

const props = defineProps<{
  organizationSlug: string;
}>();

const { t } = useI18n();
const toast = useToast();
const router = useRouter();
const { user } = useUser();
const {
  currentOrganization,
  getOrganization,
  updateOrganization,
  destroyOrganization,

  inviteOrganizationMembers,
  removeOrganizationMember,
  updateOrganizationMember,
  makeOrganizationMemberOwner,

  destroyTeam,

  ROLE_CODES
} = useOrganizations(props, { user });

const { currentTeams } = useWorkspaces(props);

const teams = currentTeams;
const organization = currentOrganization;

const data = reactive({
  newOrganizationName: "",
  newOrganizationSlug: "",
  emailsToInvite: []
});

const state = reactive({
  isEditing: false,
  isMemberInvitationPopupOpen: false,
  isExternalInvitationPopupOpen: false,
  isTeamCreationPopupOpen: false,
  isCreatingAssistants: false
});

const expired = computed(() => {
  return organization.value?.expired;
});

const organizationRole = computed(() => {
  if (!membership.value?.roleCode) return null;
  return roles.value[membership.value?.roleCode];
});

const roles = computed(() => {
  const rolesArray = organization.value?.roles || [];
  const roles: Record<string, OrganizationRole> = {};
  for (const role of rolesArray) {
    roles[role.roleCode] = role;
  }
  return roles;
});

const assignableRoles = computed(() => {
  return Object.values(roles.value)
    .map((r) => r.roleCode)
    .filter((roleCode) => !["OWNER", "ASSISTANT_OWNER"].includes(roleCode));
});

const rolesUserCanAssign = computed(() => {
  return Object.values(roles.value).filter((r) =>
    assignableRoles.value.includes(r.roleCode)
  );
});

const members = computed(() => {
  const allMembers = organization.value?.members ?? [];
  return allMembers.sort((a, b) => {
    if (a.created < b.created) {
      return -1;
    }
    if (a.created > b.created) {
      return 1;
    }
    return 0;
  });
});

const internalMembers = computed(() => {
  return members.value.filter((m) => m.roleCode != "EXTERN");
});

const externalMembers = computed(() => {
  return members.value.filter((m) => m.roleCode == "EXTERN");
});

const activeMembers = computed(() => {
  return members.value.filter((m) => m.accepted);
});

const owner = computed(() => {
  return internalMembers.value.find((m) => m.roleCode === "OWNER");
});

const assistantOwners = computed(() => {
  return internalMembers.value.filter(
    (m) => m.roleCode === "ASSISTANT_OWNER"
  );
});

const memberEmails = computed(() => {
  return members.value.map((m) => m.email);
});

const membership = computed(() => {
  return organization.value?.membership;
});

const workspaceLimit = computed(() => {
  return organization.value?.workspaceLimit ?? Infinity;
});

const licenseLimit = computed(() => {
  return organization.value?.licenseLimit ?? Infinity;
});

const renewalDateFormatted = computed(() => {
  if (!subscription.value) return null;
  if (!subscription.value.renewalDate) return null;
  const date = new Date(subscription.value.renewalDate);
  return formatDate(date, "D");
});

const canManageMembers = computed(() => {
  return !expired.value && organizationRole.value?.canManageMembers;
});

const canManageTeams = computed(() => {
  return !expired.value && organizationRole.value?.canManageTeams;
});

const dayTheOrganizationWillBeDeletedOn = computed(() => {
  if (!expired.value) return null;
  if (subscription.value?.validUntil == null) return null;
  const date = new Date(subscription.value.validUntil);
  date.setDate(date.getDate() + 180);
  return formatDate(date, "D");
});

// ==================
// ORGANIZATION NAME

const organizationNameInput = useTemplateRef("organizationNameInput");

function startEditingName() {
  if (!state.isEditing) {
    data.newOrganizationName = organization.value?.name ?? "";
    data.newOrganizationSlug = organization.value?.slug ?? "";
    state.isEditing = true;
    nextTick(() => organizationNameInput.value?.focus());
  } else {
    state.isEditing = false;
  }
}

const nameError = ref<BadRequestError | null>(null);
const savingName = ref(false);

async function saveName() {
  if (!organization.value) {
    showError("Organization not found");
    return;
  }
  savingName.value = true;
  nameError.value = null;
  try {
    const slugBefore = organization.value.slug;
    const updated = await updateOrganization(organization.value.slug, {
      name: data.newOrganizationName,
      slug: data.newOrganizationSlug
    });
    if (updated.slug != slugBefore) {
      // Redirect to new slug
      router.push({
        name: "settings-organization",
        params: {
          organizationSlug: updated.slug
        }
      });
    }
    state.isEditing = false;
  } catch (e) {
    const error = e as Error & { code?: string; status?: number; fields?: Record<string, string> };
    if ("fields" in error) {
      nameError.value = error as BadRequestError;
    }
    console.error(error.name, error.code, error.message);
    if (error.status === 400 && error.fields?.name) {
      showError(error.fields.name);
    } else {
      showError(error);
    }
  } finally {
    savingName.value = false;
  }
}

const organizationName = reactive({
  isFree: null as null | boolean,
  check: null as null | Promise<boolean>
});

async function checkOrganizationName(name: string) {
  if (name == organization.value?.name) {
    organizationName.isFree = true;
    return true;
  }

  organizationName.check = API.post<boolean>("/v1/is-organization-name-free", {
    data: { name }
  });

  try {
    const isFree = await organizationName.check;
    organizationName.isFree = isFree;
    return isFree;
  } catch (error) {
    showError(error as Error);
  }
}
const debouncedCheckOrganizationName = debounce(checkOrganizationName, 1000);

function onOrganizationNameUpdate() {
  organizationName.isFree = null;

  debouncedCheckOrganizationName(data.newOrganizationName);
}

async function tryDestroyingOrganization() {
  if (!organization.value) {
    showError("Organization not found");
    return;
  }

  if (
    (await showConfirm(t("questions.confirmDeleteOrganization"))) ==
    false
  ) {
    return;
  }
  try {
    await destroyOrganization(organization.value);
  } catch (e) {
    const error = e as Error & { code?: string; status?: number };
    console.error(error.name, error.code, error.message);
    showError(error);
  }
}

async function tryLeavingOrganization() {
  if (membership.value == null) {
    showError("Membership not found");
    return;
  }
  const organizationName = organization.value?.name;
  await tryRemovingMember(membership.value);
  toast.success(t("messages.leftOrganization", { name: organizationName }));
  router.push({ name: "models-explorer" });
}

async function updateMembersCanSeeEmails(value: boolean) {
  if (!organization.value) {
    showError("Organization not found");
    return;
  }

  try {
    await updateOrganization(organization.value.slug, {
      membersCanSeeEmails: value
    });
  } catch (error) {
    console.log(error);
    showError(error as Error);
  }
}

// MEMBER MANAGEMENT
function startInvitingMembers() {
  state.isMemberInvitationPopupOpen = true;
}
function startInvitingExternals() {
  state.isExternalInvitationPopupOpen = true;
}
async function sendInvitations(entries: { user: { email: string }; roleCode?: string }[]) {
  if (!organization.value) {
    showError("Organization not found");
    return;
  }
  return await inviteOrganizationMembers(organization.value, entries);
}
async function tryRemovingMember(member: OrganizationMember) {
  // Confirm action first:
  if (!organization.value) {
    showError("Organization not found");
    return;
  }

  if (member.id == membership.value?.id) {
    // confirm if user is leaving themself
    const question = t("questions.confirmLeavingOrganization", {
      organizationname: organization.value.name
    });
    if ((await showConfirm(question)) == false) {
      return;
    }
  } else {
    // confirm removing user
    const question = member.accepted
      ? t("questions.confirmRemoveMember", {
          name: member.name,
        })
      : t("questions.confirmRemoveAnonymousMember", {
          email: member.email
        });
    if ((await showConfirm(question)) == false) {
      return;
    }
  }

  // Remove the member
  try {
    await removeOrganizationMember(organization.value, member);
  } catch (error) {
    console.log("error removing organization member", error);
    showError(error as Error);
  }
}
async function tryUpdatingMember(memberData: { id: number; roleCode: string }) {
  if (!organization.value) {
    showError("Organization not found");
    return;
  }

  if (memberData.roleCode == "OWNER") {
    showError("Cannot set OWNER role directly.");
    return;
  } else if (memberData.id === membership.value?.id) {
    // Make sure users don't change their own role when not meaning to:
    const confirmed = await showConfirm(
      t("questions.confirmChangingOwnRole")
    );
    if (confirmed === false) return;
  }

  try {
    await updateOrganizationMember(organization.value, memberData);
  } catch (error) {
    console.log(error);
    showError(error as Error);
  }
}

// WORKSPACE CREATION
function openTeamCreationPopup() {
  state.isTeamCreationPopupOpen = true;
}

async function tryDestroyingTeam(team: { id: number; organizationSlug: string; slug: string }) {
  if (
    await showConfirm(
      t("questions.confirmDeleteTeam", team),
      t("actions.deletePermanently")
    )
  ) {
    await destroyTeam(team);
  }
}

async function setAssistantOwners(selectedMembers: { id: number }[]) {
  const selectedMembersCopy = [...selectedMembers];

  // DEGRADE old assistants to admins:
  for (const ass of assistantOwners.value) {
    const index = selectedMembersCopy.findIndex((m) => m.id === ass.id);
    if (index === -1) {
      await tryUpdatingMember({
        ...ass,
        roleCode: "ADMIN"
      });
    } else {
      selectedMembersCopy.splice(index, 1);
    }
  }

  for (const ass of selectedMembersCopy) {
    await tryUpdatingMember({
      ...ass,
      roleCode: "ASSISTANT_OWNER"
    });
  }

  state.isCreatingAssistants = false;
}

async function setOwner(newOwner: { id: number }) {
  if (!organization.value) return;

  const member = members.value.find((m) => m.id === newOwner.id);
  if (!member) {
    showError("Member not found");
    return;
  }

  const confirmed = await showConfirm(
    t("questions.confirmOwnershipTransferral", {
      newOwner: member.email
    }),
    t("actions.transferOwnership"),
    t("labels.abort"),
    {
      content: [{
        type: "text",
        text: t("messages.notUndoable"),
        class: 'message-warning p-2 mb-4'
      }]
    }
  );
  if (confirmed === false) {
    state.isCreatingAssistants = false;
    return;
  }

  try {
    await makeOrganizationMemberOwner(organization.value, member.id);
    state.isCreatingAssistants = false;
    await getOrganization(organization.value.slug);
  } catch (error) {
    console.log(error);
    showError(error as Error);
  }
}

async function tryReactivatingSubscription() {
  if (!subscription.value) {
    showError("Subscription not available.");
    return;
  }

  if (
    (await showConfirm(
      t("questions.confirmReactivatingSubscription"),
      t("actions.confirmBuyingRenewal"),
      t("labels.no"),
      { title: t("actions.enableRenewal") }
    )) == false
  ) {
    return;
  }

  try {
    await subscriptionStore.renewSubscription(subscription.value);
  } catch (error) {
    showError(error as Error);
  }
}

// SUBSCRIPTION
const subscription = ref<Subscription | null>(null);
const subscriptionStore = useSubscriptions();

async function loadSubscription() {
  if (!organizationRole.value?.canManageOrganization) {
    subscription.value = null;
    return;
  }
  
  const subs = await subscriptionStore.getSubscriptions();
  const sub = subs.find((s) => s.id === organization.value?.subscriptionId);
  subscription.value = sub ?? null;
}

watch(() => organizationRole.value?.roleCode, loadSubscription, { immediate: true });
</script>

<style lang="scss">
.view-organizationsettings {
  display: block;

  &__header {
    display: flex;
    gap: 0.5rem;
    align-items: center;
  }

  &__editform {
    position: relative;
    display: flex;
    max-width: 50rem;
    flex-direction: column;
    gap: 1rem;
    align-items: start;
    background-color: var(--color-background-deep);
    padding: 1.5rem;
    border-radius: 0.5rem;
    filter: drop-shadow(0 0.5rem 1rem var(--color-shadow-1));

    &::before {
      content: "";
      position: absolute;
      top: -0.5rem;
      left: 1rem;
      width: 0;
      height: 0;
      border-left: 0.5rem solid transparent;
      border-right: 0.5rem solid transparent;
      border-bottom: 0.5rem solid var(--color-background-deep);
    }
  }

  &__invitepopup {
    padding: 2rem;
  }

  &__teams {
    --gap: 4px;
    max-width: 1600px;

    tr {
      grid-template-columns: 1fr min-content 3rem;
      gap: 1rem;
      padding-left: 1rem;
    }
  }
  &__team {
    background-color: var(--color-background-deep);
    border-radius: 5px;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  }
  &__teammembers {
    display: flex;
    flex-direction: row-reverse;
  }
  &__teammember {
    & + & {
      margin-right: -0.5rem;
    }
  }

  &__join {
    margin: auto;
    width: max-content;
  }

  &__assistant {
    padding: 0.5rem 0.5rem;
    background-color: var(--color-background-deep);
    box-shadow: 0 2px 5px rgba(00, 0, 0, 0.1);
    border-radius: 5px;
    display: flex;
    align-items: center;
    gap: 0.5rem;

    & + & {
      margin-top: 0.5rem;
    }
  }
}
</style>
